/**
 * lib/analytics.ts
 *
 * The app-side pure helpers behind parent-client's PostHog instrumentation
 * (docs/plans/analytics-implementation-plan.md §6.2, §7.1). Everything that
 * needs a Capacitor listener, posthog-js, or the router instance lives
 * elsewhere (`lib/app-open.ts`, `lib/analytics-router.ts`, `main.tsx`) — this
 * module is deliberately dependency-free so its decision logic is unit
 * testable without mocking Capacitor or the SDK (see `analytics.test.ts`).
 * Mirrors `apps/student-client/src/lib/analytics.ts`.
 *
 * Ground rule 1: nothing in here may be load-bearing. Every function is a
 * pure computation or a guarded localStorage read.
 *
 * Phase 2.7 (B1): `VerifyOtpResponse.created_at` (`lib/api.ts`) now carries
 * `parents.created_at`, mirroring student-api's `LoginResponse.created_at` —
 * see that file's `setSignupDate`/`clearSignupDate` for the reference this
 * was ported from, byte-for-byte identical apart from the storage key.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Collapse a concrete API path into its route TEMPLATE. Ported from
 * `apps/student-client/src/lib/analytics.ts` (Phase 2.2) — kept
 * byte-for-byte identical so the two never drift.
 *
 * Callers pass resolved paths (`/children/1f0c…/reports/42`), so without
 * this one event name explodes into thousands of distinct `path` values and
 * both PostHog's UI and any funnel over it become useless
 * (docs/plans/analytics-implementation-plan.md §6.3). Query strings are
 * dropped entirely — they carry free text often enough that keeping them is
 * a PII risk, not just noise.
 */
export function templatizePath(path: string): string {
  const [pathOnly] = path.split(/[?#]/);
  const templated = pathOnly
    .split('/')
    .map((segment) => (UUID_RE.test(segment) || /^\d+$/.test(segment) ? ':id' : segment))
    .join('/');
  return templated || '/';
}

/**
 * A resume this soon after backgrounding is not a return visit — an incoming
 * SMS during OTP entry is the canonical case here and happens constantly
 * (docs/plans/analytics-implementation-plan.md §6.2, applied to parent via
 * §7.1). Anything shorter is suppressed; exactly 30s counts.
 */
export const RESUME_DEBOUNCE_SECONDS = 30;

/** True when a foreground-after-background should emit `parent_app_opened`. */
export function shouldCountResume(secondsBackgrounded: number): boolean {
  if (!Number.isFinite(secondsBackgrounded)) return false;
  return secondsBackgrounded >= RESUME_DEBOUNCE_SECONDS;
}

/** Whole days between two instants, floored, never negative. */
export function daysSince(from: Date, now: Date): number {
  const ms = now.getTime() - from.getTime();
  if (!Number.isFinite(ms) || ms < 0) return 0;
  return Math.floor(ms / 86_400_000);
}

// ── Session context (§6.2, §7.1) ─────────────────────────────────────────────
// `days_since_signup` and `is_first_session` ride on every event.
//
// The signup date is server-authoritative: `VerifyOtpResponse.created_at`
// carries `parents.created_at` straight from Postgres. The client just
// caches it locally so every event can ride `days_since_signup` without an
// extra round trip per capture call. It is re-stamped (and overwritten
// unconditionally — the server value is always correct) at every login, and
// cleared on logout for shared-device hygiene, same as the token itself.
// Parent's JWT is also a 7-day token (`apps/parent-api/src/routes/auth.ts`,
// `expiresIn: '7d'`) — same self-healing timescale as student, so no
// cold-start backfill fetch is needed here either (see this phase's report).

const SIGNUP_AT_KEY = 'tupai_parent_signup_at';
const OPENED_KEY = 'tupai_parent_analytics_opened';

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Stamp the signup date from the server's `created_at`. Called from every
 * login path (`store/authStore.ts`'s `login()`). Writes UNCONDITIONALLY
 * whenever given a usable value — the server value is authoritative. An
 * unparseable or missing value (older server, or a pre-Phase-2.7 cached
 * response) is simply not written, never guessed.
 */
export function setSignupDate(createdAt: string | null | undefined): void {
  if (!createdAt) return;
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return;
  const store = safeStorage();
  if (!store) return;
  try {
    store.setItem(SIGNUP_AT_KEY, parsed.toISOString());
  } catch {
    // Analytics is never load-bearing.
  }
}

/**
 * Drop the signup stamp when the session ends. Called from `logout()` in
 * `store/authStore.ts`. Without this, the next parent to sign in on a shared
 * device would inherit the previous parent's signup date. Restored from the
 * server at the next login, never lost for good.
 */
export function clearSignupDate(): void {
  const store = safeStorage();
  if (!store) return;
  try {
    store.removeItem(SIGNUP_AT_KEY);
  } catch {
    // Analytics is never load-bearing.
  }
}

export interface SessionContext {
  daysSinceSignup?: number;
  isFirstSession?: boolean;
}

/** `daysSinceSignup` is absent when no trustworthy signup date exists. */
export function readSessionContext(now: Date = new Date()): SessionContext {
  const store = safeStorage();
  if (!store) return {};
  let daysSinceSignup: number | undefined;
  try {
    const stamp = store.getItem(SIGNUP_AT_KEY);
    if (stamp) {
      const parsed = new Date(stamp);
      if (!Number.isNaN(parsed.getTime())) daysSinceSignup = daysSince(parsed, now);
    }
  } catch {
    // ignore
  }
  let isFirstSession: boolean | undefined;
  try {
    isFirstSession = store.getItem(OPENED_KEY) !== '1';
  } catch {
    // ignore
  }
  return { daysSinceSignup, isFirstSession };
}

/** Called once the first `parent_app_opened` of this install has been emitted. */
export function markSessionOpened(): void {
  const store = safeStorage();
  if (!store) return;
  try {
    store.setItem(OPENED_KEY, '1');
  } catch {
    // ignore
  }
}
